import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiSave, FiSettings, FiShare2, FiGrid, FiCode, FiTerminal, FiPlus, FiChevronRight, FiCpu, FiShield, FiDatabase, FiActivity, FiZap } = FiIcons;

const Builder = () => {
  const [viewMode, setViewMode] = useState('canvas'); // 'canvas' or 'code'
  const [agentOpen, setAgentOpen] = useState(true);

  const nodes = [
    { id: 1, type: 'trigger', label: 'Patient Intake', icon: FiActivity, color: 'bg-brand-mint/20' },
    { id: 2, type: 'nlp', label: 'Venice NLP', icon: FiCpu, color: 'bg-brand-indigo/10' },
    { id: 3, type: 'gate', label: 'Clinical Review', icon: FiShield, color: 'bg-yellow-50' },
    { id: 4, type: 'action', label: 'Update Vault', icon: FiDatabase, color: 'bg-blue-50' }
  ];

  return (
    <div className="h-screen bg-brand-ivory flex flex-col overflow-hidden">
      {/* Top Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0 z-50">
        <div className="flex items-center space-x-6">
          <Link to="/dashboard" className="text-2xl font-display font-bold text-brand-indigo hover:opacity-80 transition-opacity">
            hd.ai
          </Link>
          <div className="h-6 w-[1px] bg-gray-200" />
          <div className="flex items-center space-x-2">
            <span className="text-sm font-bold text-brand-indigo">Cardiac Triage v2</span>
            <span className="text-[10px] font-bold px-2 py-0.5 bg-brand-mint/20 text-brand-indigo rounded-full uppercase tracking-widest">Draft</span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 p-1 rounded-xl mr-4">
            <button 
              onClick={() => setViewMode('canvas')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 ${viewMode === 'canvas' ? 'bg-white shadow-sm text-brand-indigo' : 'text-gray-400 hover:text-brand-indigo'}`}
            >
              <SafeIcon icon={FiGrid} />
              <span>Canvas</span>
            </button>
            <button 
              onClick={() => setViewMode('code')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 ${viewMode === 'code' ? 'bg-white shadow-sm text-brand-indigo' : 'text-gray-400 hover:text-brand-indigo'}`}
            >
              <SafeIcon icon={FiCode} />
              <span>.hd DSL</span>
            </button>
          </div>
          <button className="p-2 text-brand-indigo hover:bg-gray-50 rounded-xl transition-all">
            <SafeIcon icon={FiSettings} />
          </button>
          <button className="flex items-center space-x-2 bg-brand-indigo text-white px-5 py-2 rounded-xl font-bold text-sm hover:bg-brand-slate transition-all shadow-lg">
            <SafeIcon icon={FiPlay} />
            <span>Deploy</span>
          </button>
        </div>
      </header>

      <div className="flex flex-grow overflow-hidden relative">
        {/* Left Sidebar: Palette */}
        <aside className="w-16 hover:w-64 bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden flex flex-col group z-40">
          <div className="p-4 flex-grow space-y-6">
            <div className="space-y-2">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity mb-4">Core Blocks</div>
              {nodes.map(node => (
                <div key={node.id} className="flex items-center space-x-4 p-2 cursor-pointer hover:bg-brand-ivory rounded-xl transition-all">
                  <div className={`w-8 h-8 ${node.color} rounded-lg flex items-center justify-center shrink-0`}>
                    <SafeIcon icon={node.icon} className="text-brand-indigo" />
                  </div>
                  <span className="text-sm font-bold text-brand-indigo whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">{node.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center space-x-4 p-2 cursor-pointer hover:bg-brand-ivory rounded-xl transition-all">
              <div className="w-8 h-8 bg-brand-mint text-brand-indigo rounded-lg flex items-center justify-center shrink-0">
                <SafeIcon icon={FiPlus} />
              </div>
              <span className="text-sm font-bold text-brand-indigo whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">Import .hdpkg</span>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-grow relative overflow-hidden bg-brand-ivory">
          {viewMode === 'canvas' ? (
            <div className="absolute inset-0 p-12 overflow-auto grid place-items-center">
              {/* Canvas Visualization */}
              <div className="flex flex-col items-center space-y-12">
                {nodes.map((node, i) => (
                  <React.Fragment key={node.id}>
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="w-64 bg-white p-6 rounded-[2rem] border-2 border-transparent hover:border-brand-mint shadow-sm hover:shadow-xl transition-all cursor-pointer group"
                    >
                      <div className={`w-12 h-12 ${node.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <SafeIcon icon={node.icon} className="text-brand-indigo text-xl" />
                      </div>
                      <div className="font-bold text-brand-indigo">{node.label}</div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{node.type}</div>
                    </motion.div>
                    {i < nodes.length - 1 && (
                      <div className="w-[2px] h-12 bg-gray-200 relative">
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-200 rounded-full" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {/* Dots Pattern */}
              <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#2E305C 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col font-mono text-sm">
              <div className="flex-grow p-12 bg-brand-indigo text-white overflow-auto">
                <div className="text-brand-mint">@jurisdiction("us")</div>
                <div className="text-brand-mint">@workflow("cardiac_triage_v2")</div>
                <div className="mt-4 text-white/50">{`// Trigger: Patient Intake API`}</div>
                <div>trigger Intake {`{`}</div>
                <div className="pl-6">endpoint = "/api/v1/intake"</div>
                <div className="pl-6">schema = ./schemas/patient.json</div>
                <div>{`}`}</div>
                
                <div className="mt-6 text-white/50">{`// NLP Layer: Venice-Clinical`}</div>
                <div>node Analysis {`{`}</div>
                <div className="pl-6">engine = venice.clinical_v4</div>
                <div className="pl-6">prompt = "Assess acuity for: {`{{ intake.symptoms }}`}"</div>
                <div className="pl-6">temperature = 0.2</div>
                <div>{`}`}</div>

                <div className="mt-6 text-white/50">{`// Security Gate`}</div>
                <div className="text-brand-mint">node Review {`{`}</div>
                <div className="pl-6">logic = rule_engine.verify_acuity</div>
                <div className="pl-6 text-brand-mint">require("clinician_approval")</div>
                <div>{`}`}</div>
              </div>
            </div>
          )}
        </main>

        {/* Right Sidebar: Agent Helper */}
        <aside className={`transition-all duration-500 ease-in-out border-l border-gray-200 bg-white z-40 flex flex-col ${agentOpen ? 'w-80' : 'w-0 overflow-hidden'}`}>
          <div className="p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display font-bold text-brand-indigo flex items-center space-x-2">
                <SafeIcon icon={FiZap} className="text-brand-mint" />
                <span>Agent Mode</span>
              </h3>
              <button 
                onClick={() => setAgentOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>

            <div className="flex-grow space-y-6 overflow-y-auto">
              <div className="bg-brand-ivory p-4 rounded-2xl border border-gray-100">
                <p className="text-xs text-brand-slate leading-relaxed">
                  "I've detected this workflow handles PHI. Would you like me to automatically add a **Vault Storage** node after the Review gate?"
                </p>
                <div className="flex space-x-2 mt-4">
                  <button className="flex-grow py-2 bg-brand-indigo text-white text-[10px] font-bold rounded-lg">Yes, add node</button>
                  <button className="px-3 py-2 bg-white text-brand-indigo border border-gray-100 text-[10px] font-bold rounded-lg">Dismiss</button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Suggestions</div>
                {['Add HIPAA Header', 'Optimize Prompt', 'Retry Logic'].map(s => (
                  <button key={s} className="w-full text-left p-3 hover:bg-brand-ivory rounded-xl text-xs font-bold text-brand-indigo transition-all flex items-center justify-between group">
                    <span>{s}</span>
                    <SafeIcon icon={FiPlus} className="opacity-0 group-hover:opacity-100" />
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-6 border-t border-gray-100">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Ask Agent..." 
                  className="w-full pl-4 pr-10 py-3 bg-brand-ivory border-transparent rounded-xl text-xs focus:ring-2 focus:ring-brand-mint transition-all"
                />
                <button className="absolute right-3 top-2.5 text-brand-indigo">
                  <SafeIcon icon={FiZap} />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Floating Toggle for Agent */}
        {!agentOpen && (
          <button 
            onClick={() => setAgentOpen(true)}
            className="absolute right-6 bottom-6 w-14 h-14 bg-brand-indigo text-white rounded-2xl shadow-2xl flex items-center justify-center hover:scale-110 transition-all z-50 animate-bounce"
          >
            <SafeIcon icon={FiZap} className="text-xl" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Builder;